// RC2 Sprint 0 · Reachability — Punch Detail
// Loads a single punch item by id and renders status/comments/timeline.
// Reuses GET /api/punch-items/{id} (Iter 82.2). No new endpoints.
import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  ArrowLeft, AlertTriangle, CheckCircle2, Clock, Circle, Printer,
  MessageSquare, User as UserIcon, Send,
} from "lucide-react";
import { api } from "@/lib/api";
import { enqueue as offlineEnqueue, listQueue } from "@/lib/offlineQueueV2";
import AuditTimelinePanel from "@/components/AuditTimelinePanel";

const STATUS_ICON = {
  open: Circle,
  in_progress: Clock,
  ready_for_review: AlertTriangle,
  closed: CheckCircle2,
  rejected: CheckCircle2,
};

const STATUS_COLOR = {
  open: "text-[var(--fop-text-2)]",
  in_progress: "text-orange-400",
  ready_for_review: "text-yellow-400",
  closed: "text-emerald-500",
  rejected: "text-red-500",
};

export default function PunchDetail() {
  const { projectId, punchId } = useParams();
  const navigate = useNavigate();
  const [punch, setPunch] = useState(null);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [commentDraft, setCommentDraft] = useState("");
  const [posting, setPosting] = useState(false);
  const commentOpIdRef = useRef(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [p, c] = await Promise.all([
        api.get(`/punch-items/${punchId}`).then((r) => r.data).catch((e) => (
          // Distinguish a genuine server rejection (404/403 — this
          // punch item really doesn't exist or isn't accessible) from
          // a network-level failure (offline, no response at all) —
          // only the latter should fall back to an offline placeholder;
          // the former must still show "not found".
          e?.response ? null : "NETWORK_FAILURE"
        )),
        api.get(`/punch-items/${punchId}/comments`).then((r) => r.data?.comments || r.data || []).catch(() => []),
      ]);
      // FP-034 bug fix (testing_agent_v3_fork wave-b-turn5) — when the
      // punch GET fails due to a NETWORK-level error (offline,
      // transient error — not a real 404/403), the whole comments+
      // add-comment-form section used to be hidden behind
      // `!loading && punch && (...)`, so a user's own queued, pending
      // comment for THIS punch item vanished from view on reload even
      // though it was safely persisted in the offline queue the whole
      // time. A synthetic offline placeholder keeps the same JSX
      // branch (and therefore the comment form + pending merge)
      // rendering — but only for that network-failure case; a real
      // 404/403 still correctly shows "not found".
      setPunch(
        p === "NETWORK_FAILURE"
          ? { id: punchId, title: "(offline — punch details not cached)",
              status: "open", _offline_placeholder: true }
          : p
      );
      // A full page reload (or an offline comments fetch, caught
      // above) must not lose a durably-queued-but-unsynced comment;
      // merge in anything still pending for this punch item from the
      // canonical v2 queue — unconditionally, regardless of whether
      // the server fetches above succeeded.
      const pendingFromQueue = listQueue()
        .filter((e) => e.op === "comment.create" && e.payload?.url === `/punch-items/${punchId}/comments`)
        .map((e) => ({
          id: e.id, body: e.payload.body?.body, author_name: "You",
          created_at: new Date(e.queued_at).toISOString(), _pending_sync: true,
        }));
      setComments([...c, ...pendingFromQueue]);
    } finally { setLoading(false); }
  }, [punchId]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // FP-034 (2026-09-09 audit remediation) — reconcile after ANY
  // offline-queue drain (manual or automatic background sync), so a
  // queued comment that finally lands on the server replaces the
  // optimistic pending row with the real one.
  useEffect(() => {
    const onSynced = () => { fetchAll(); };
    window.addEventListener("forgedops:offline-synced", onSynced);
    return () => window.removeEventListener("forgedops:offline-synced", onSynced);
  }, [fetchAll]);

  const addComment = async () => {
    const draft = commentDraft.trim();
    if (!draft || !punch) return;
    // Stable id reused across manual retries of the same draft so a
    // retry (online) never double-posts.
    if (!commentOpIdRef.current) {
      commentOpIdRef.current = (typeof crypto !== "undefined" && crypto.randomUUID)
        ? crypto.randomUUID() : `cop_${Date.now()}_${Math.floor(Math.random() * 1e9)}`;
    }
    const opId = commentOpIdRef.current;
    setPosting(true);
    try {
      // FP-034 — punch comments now durably queue offline through the
      // SAME canonical v2 queue used by markups/punch items/pins/
      // objects (no new queue). The entry is written to disk
      // (enqueue() is a synchronous localStorage write) BEFORE the
      // input is cleared or anything is shown as saved.
      if (typeof navigator !== "undefined" && navigator.onLine === false) {
        const localId = offlineEnqueue("comment.create", {
          url: `/punch-items/${punch.id}/comments`,
          body: { body: draft },
        });
        commentOpIdRef.current = null;
        setCommentDraft("");
        setComments((c) => [...c, {
          id: localId, body: draft, author_name: "You",
          created_at: new Date().toISOString(), _pending_sync: true,
        }]);
        return;
      }
      await api.post(`/punch-items/${punch.id}/comments`,
        { body: draft, client_op_id: opId });
      commentOpIdRef.current = null;
      setCommentDraft("");
      await fetchAll();
    } catch (e) { /* leave draft in place so the user can retry */ }
    finally { setPosting(false); }
  };

  const Icon = STATUS_ICON[punch?.status] || Circle;

  return (
    <div className="min-h-screen bg-[var(--fop-bg,#0a0a0b)] text-[var(--fop-text)]" data-testid="punch-detail">
      <style>{`@media print {
        body, [data-testid="punch-detail"] { background: white !important; color: black !important; }
        [data-no-print] { display: none !important; }
      }`}</style>
      <div className="max-w-3xl mx-auto px-6 py-8">
        <div className="mb-4 flex items-center justify-between" data-no-print>
          <button
            onClick={() => navigate(`/project/${projectId}/punch`)}
            className="inline-flex items-center gap-1 text-[11px] font-mono uppercase tracking-wider text-[var(--fop-text-3)] hover:text-[var(--fop-text)]"
            data-testid="punch-back-btn"
          >
            <ArrowLeft size={12} /> Punch list
          </button>
          <button
            onClick={() => window.print()}
            data-testid="punch-detail-print-btn"
            className="inline-flex items-center gap-2 px-3 py-2 rounded border border-[var(--fop-primary)]/40 text-[12px] font-mono uppercase tracking-wider text-[var(--fop-primary)] hover:bg-[var(--fop-primary)]/10"
          >
            <Printer size={14} /> Print
          </button>
        </div>

        {loading && (
          <div className="text-center py-12 text-[var(--fop-text-3)]" data-testid="punch-detail-loading">Loading punch item…</div>
        )}

        {!loading && !punch && (
          <div className="text-center py-12" data-testid="punch-detail-missing">
            <AlertTriangle size={28} className="mx-auto text-orange-400 mb-2" />
            <div className="text-sm text-[var(--fop-text-2)]">Punch item not found, or you don't have access.</div>
            <Link to="/punch" className="inline-block mt-3 text-[11px] font-mono uppercase tracking-wider text-[var(--fop-primary)]">Back to Punch Center →</Link>
          </div>
        )}

        {!loading && punch && (
          <>
            <header className="mb-6 pb-4 border-b border-[var(--fop-border)]">
              <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-[var(--fop-primary)]">
                Punch · {punch.id?.slice(0, 18)}
              </div>
              <h1 className="text-2xl font-semibold mt-2">{punch.title}</h1>
              {punch.description && (
                <div className="mt-2 text-sm text-[var(--fop-text-2)] leading-relaxed">
                  {punch.description}
                </div>
              )}
            </header>

            <div className="grid grid-cols-2 gap-3 mb-6">
              <Field label="Status">
                <span className={`inline-flex items-center gap-1.5 font-mono uppercase tracking-wider text-[12px] ${STATUS_COLOR[punch.status]}`}>
                  <Icon size={14} /> {(punch.status || "").replace("_", " ")}
                </span>
              </Field>
              <Field label="Priority">
                <span className="font-mono uppercase tracking-wider text-[12px] text-[var(--fop-text-2)]">
                  {punch.priority || "normal"}
                </span>
              </Field>
              <Field label="Assignee">
                <span className="text-sm text-[var(--fop-text-2)] inline-flex items-center gap-1.5">
                  <UserIcon size={12} /> {punch.assigned_to_name || punch.assigned_to_user_id || "—"}
                </span>
              </Field>
              <Field label="Due">
                <span className="font-mono text-[12px] text-[var(--fop-text-2)]">
                  {punch.due_date ? String(punch.due_date).slice(0, 10) : "—"}
                </span>
              </Field>
              <Field label="Created">
                <span className="font-mono text-[12px] text-[var(--fop-text-3)]">
                  {punch.created_at ? String(punch.created_at).slice(0, 19).replace("T", " ") : "—"}
                </span>
              </Field>
              <Field label="Updated">
                <span className="font-mono text-[12px] text-[var(--fop-text-3)]">
                  {punch.updated_at ? String(punch.updated_at).slice(0, 19).replace("T", " ") : "—"}
                </span>
              </Field>
            </div>

            <section>
              <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[var(--fop-primary)] mb-2">
                Comments · {comments.length}
              </div>
              {comments.length === 0 ? (
                <div className="text-sm text-[var(--fop-text-3)] italic">No comments yet.</div>
              ) : (
                <ul className="space-y-3" data-testid="punch-detail-comments">
                  {comments.map((c) => (
                    <li key={c.id} className="border-l-2 border-[var(--fop-primary)]/40 pl-3 py-1" data-testid={`punch-comment-${c.id}`}>
                      <div className="text-[11px] font-mono uppercase tracking-wider text-[var(--fop-text-3)]">
                        {c.author_name || c.created_by_user_id || "—"} · {c.created_at?.slice(0, 16).replace("T", " ")}
                        {c._pending_sync && (
                          <span className="ml-1.5 text-[var(--fop-text-3)]" data-testid={`punch-comment-pending-${c.id}`}>
                            · pending sync
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-[var(--fop-text)] whitespace-pre-wrap mt-0.5">{c.body}</div>
                    </li>
                  ))}
                </ul>
              )}
              <div className="mt-4 flex items-start gap-2" data-no-print>
                <textarea
                  value={commentDraft}
                  onChange={(e) => setCommentDraft(e.target.value)}
                  placeholder="Add a comment…"
                  rows={2}
                  className="flex-1 bg-[var(--fop-surface)] border border-[var(--fop-border)] rounded px-3 py-2 text-sm text-[var(--fop-text)] resize-none"
                  data-testid="punch-comment-input"
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) { e.preventDefault(); addComment(); }
                  }}
                />
                <button
                  type="button"
                  onClick={addComment}
                  disabled={posting || !commentDraft.trim()}
                  className="btn btn-primary inline-flex items-center gap-1.5 text-xs px-3 py-2"
                  data-testid="punch-comment-submit-btn"
                >
                  <Send size={13} /> {posting ? "Sending…" : "Send"}
                </button>
              </div>
            </section>

            <section className="mt-6" data-no-print>
              <AuditTimelinePanel targetType="punch_item" targetId={punch.id} limit={50} />
            </section>
          </>
        )}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="px-3 py-2 rounded bg-[var(--fop-surface)] border border-[var(--fop-border)]">
      <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--fop-text-3)]">{label}</div>
      <div className="mt-1">{children}</div>
    </div>
  );
}
